import { SERPENT_SEGS } from '../../models/shell-serpent.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * Sierpe del caparazón (models/shell-serpent.ts), both looks.
 *
 * Emerged (`shellSerpent`): the body is a chain seg1…seg6 rising out of the crater, then head and jaw. +X on a
 * segment pitches everything above it forward (toward +Z), −X rears it back; Z bends it to the side. +X on the jaw
 * opens it (it rests half open on the acid), −X closes it. Moving seg1 moves the whole body: `emerge` starts with
 * it sunk below the ground and `dive` ends there (the crater stays). The idle is a slow swaying S with the head
 * held steady, like a cobra's.
 *
 * Blows: attack (a snap) lands at 0.13 s (it starts drawn back); bite (the acid bite on a tower) strikes at 0.45 s
 * and the jaws close on it at 0.55 s; emerge breaks the ground at 0.1 s and is up at 0.75 s.
 *
 * Underground (`shellSerpentBurrow`): the ridge heaves on each push (a squash of the whole mound), the plates rise
 * and sink a beat behind it and the dust puffs.
 */

type Seg = `seg${1 | 2 | 3 | 4 | 5 | 6}`;
type B = 'root' | 'crater' | Seg | 'head' | 'jaw';
type P = KeyPose<B>;
const SEGS: Seg[] = Array.from({ length: SERPENT_SEGS }, (_, i) => `seg${i + 1}` as Seg);

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** A curl down the chain: `x` pitch and `z` side bend per segment, growing toward the neck by `grow`. */
const curl = (x: number, z = 0, grow = 0.3): P => {
  const o: P = {};
  SEGS.forEach((s, i) => {
    const w = 1 + grow * (i - 2.5);
    o[s] = [x * w, 0, z * w];
  });
  return o;
};

/** The S sway: a wave travelling up the body; the head turns against it to stay on its mark. */
const sway = (b: Bones<B>, t: number, dur: number, amp: number): void => {
  const p = phase(t, dur) * TAU;
  let sum = 0;
  SEGS.forEach((s, i) => {
    const z = amp * Math.sin(p - i * 0.75);
    sum += z;
    b[s].rot(0.25 * amp * Math.sin(2 * p - i * 0.6), 0, z);
  });
  b.head.rot(-0.2 * amp * Math.sin(2 * p - 4), 0.6 * amp * Math.sin(p - 3.5), -0.7 * sum);
};

const idle = (): Clip<B> =>
  clip<B>('idle', 3.6, true, (b, t) => {
    sway(b, t, 3.6, 0.06);
    const p = phase(t, 3.6) * TAU;
    b.jaw.rot(0.08 * Math.sin(p * 2) - 0.05);
  });

/** Moving while up: the sway grows and quickens, the head weaving. */
const walk = (): Clip<B> =>
  clip<B>('walk', 1.6, true, (b, t) => {
    sway(b, t, 1.6, 0.11);
    b.jaw.rot(-0.1);
  });

/** Drawn back, coiled to strike: the neck pulled back in an S, the head high, the jaws shut. */
const COIL: P = { ...curl(-0.08, 0, 0.6), seg2: [0.05, 0, 0], seg3: [0.1, 0, 0], head: [-0.35, 0, 0], jaw: [-0.3, 0, 0] };
/** The strike: the whole neck thrown forward and down, the head driving in, the jaws gaping. */
const STRIKE: P = { seg1: [0.05, 0, 0, 0, 0, 0.08], seg2: [0.12, 0, 0], seg3: [0.2, 0, 0], seg4: [0.3, 0, 0], seg5: [0.3, 0, 0], seg6: [0.2, 0, 0], head: [0.35, 0, 0], jaw: [0.4, 0, 0] };

const attack = (): Clip<B> =>
  clip<B>('attack', 0.9, false, (b, t) => {
    timeline(b, t, [
      [0, COIL],
      [0.06, { ...COIL, head: [-0.42, 0, 0] }, outQuad],
      [0.13, STRIKE, inCubic],
      [0.2, { ...STRIKE, jaw: [-0.38, 0, 0] }, outExpo],
      [0.9, {}, inOut],
    ], { head: 0.02 });
  });

/** The acid bite: it draws back slow, hangs coiled, then strikes (0.45 s) and clamps (0.55 s), worrying its prey. */
const bite = (): Clip<B> =>
  clip<B>('bite', 1.4, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.32, { ...COIL, jaw: [0.35, 0, 0] }, inOut],
      [0.38, { ...COIL, head: [-0.45, 0, 0], jaw: [0.5, 0, 0] }, outQuad],
      [0.45, { ...STRIKE, seg5: [0.4, 0, 0], head: [0.45, 0, 0], jaw: [0.55, 0, 0] }, inCubic],
      [0.55, { ...STRIKE, seg5: [0.4, 0, 0], head: [0.5, 0, 0], jaw: [-0.4, 0, 0] }, outExpo],
      [0.95, { ...STRIKE, seg5: [0.38, 0, 0], head: [0.48, 0, 0], jaw: [-0.4, 0, 0] }],
      [1.4, {}, inOut],
    ], { head: 0.03 });
    // It worries its bite side to side.
    const w = envelope(t, 0.55, 0.62, 0.85, 0.95);
    b.head.rot(0, 0, 0.18 * Math.sin((t - 0.55) * 30) * w);
    b.seg6.rot(0, 0, 0.08 * Math.sin((t - 0.55) * 30 - 0.6) * w);
  }, (t) => thud(t, [[0.55, 0.015]]), 'Mordida ácida');

/** Up out of the ground: the crater bursts (0.1 s), the body shoots up (0.75 s), it rears with its jaws wide and settles. */
const emerge = (): Clip<B> =>
  clip<B>('emerge', 1.7, false, (b, t) => {
    const under: P = { ...curl(-0.15, 0.1, 0.4), seg1: [0, 0, 0, 0, -2.4, -0.1], head: [-0.6, 0, 0], jaw: [-0.3, 0, 0] };
    timeline(b, t, [
      [0, under],
      [0.15, { ...under, seg1: [0, 0, 0, 0, -1.9, -0.1] }, inQuad],
      [0.75, { ...curl(-0.1, -0.05, 0.4), seg1: [0, 0, 0, 0, 0.12, 0], head: [-0.55, 0, 0], jaw: [0.6, 0, 0] }, outCubic],
      [1.05, { ...curl(-0.06, 0.02, 0.4), seg1: [0, 0, 0, 0, -0.02, 0], head: [-0.4, 0, 0], jaw: [0.65, 0, 0] }, inOut],
      [1.7, {}, inOut],
    ], { head: 0.05, jaw: 0.03 });
    // The crater bursts up out of the flat ground.
    b.crater.scale(0.15 + 0.85 * span(t, 0.05, 0.35, outBack)).pos(0, 0.06 * kick(t, 0.1, 5, 8), 0);
    // It shakes the earth off as it rises.
    const sh = envelope(t, 0.3, 0.45, 0.8, 1.0) * Math.sin(t * 42);
    b.seg3.rot(0, 0, 0.04 * sh);
    b.seg5.rot(0, 0, -0.05 * sh);
  }, (t) => thud(t, [[0.1, 0.05], [0.75, 0.02]]), 'Emerge');

/** Back down: it rears, then pours itself back into its hole head last. */
const dive = (): Clip<B> =>
  clip<B>('dive', 1.2, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.3, { ...curl(-0.08, 0, 0.4), head: [-0.3, 0, 0], jaw: [-0.3, 0, 0] }, inOut],
      [1.0, { ...curl(0.12, 0.05, 0.4), seg1: [0, 0, 0, 0, -2.4, -0.05], head: [0.5, 0, 0], jaw: [-0.35, 0, 0] }, inCubic],
      [1.2, { ...curl(0.12, 0.05, 0.4), seg1: [0, 0, 0, 0, -2.4, -0.05], head: [0.5, 0, 0], jaw: [-0.35, 0, 0] }],
    ]);
  }, undefined, 'Se hunde');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    SEGS.forEach((s, i) => b[s].rot(-0.05 * q * (i / 3), 0, 0.04 * q));
    b.head.rot(-0.3 * q, 0.15 * q, 0.1 * q);
    b.jaw.rot(0.25 * q);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    SEGS.forEach((s, i) => b[s].rot(0.05 + 0.03 * Math.sin(p - i * 0.5), 0, 0.08 * Math.sin(p - i * 0.6)));
    b.head.rot(0.35 + 0.1 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.2 * Math.sin(p + 0.8));
    b.jaw.rot(0.15);
  });

/** The death (2.4 s): it rears and shrieks, thrashes, then crashes down across the ground (1.35 s) and lies broken. */
const die = (): Clip<B> =>
  clip<B>('die', 2.4, false, (b, t) => {
    const down: P = { seg1: [0.1, 0, 0.45, 0, -0.35, 0], seg2: [0.25, 0, 0.3], seg3: [0.35, 0, 0.2], seg4: [0.35, 0, -0.15], seg5: [0.3, 0, -0.25], seg6: [0.2, 0, -0.2], head: [0.1, 0.3, 0.4], jaw: [0.55, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.3, { ...curl(-0.1, 0, 0.5), head: [-0.6, 0, 0], jaw: [0.75, 0, 0] }, outExpo],
      [0.85, { ...curl(-0.02, 0.12, 0.6), head: [-0.2, 0.3, 0.3], jaw: [0.5, 0, 0] }, inOut],
      [1.35, down, inCubic],
      [1.55, { ...down, seg4: [0.32, 0, -0.13], seg6: [0.18, 0, -0.18] }, outBack],
      [2.4, { ...down, seg1: [0.1, 0, 0.45, 0, -0.42, 0], jaw: [0.45, 0, 0] }, inOut],
    ], { head: 0.05, seg6: 0.03 });
    // The thrash.
    const th = envelope(t, 0.3, 0.45, 0.85, 1.0) * Math.sin((t - 0.3) * 24);
    SEGS.forEach((s, i) => b[s].rot(0, 0, 0.05 * th * (i % 2 ? 1 : -1)));
  }, (t) => thud(t, [[1.35, 0.05]]));

/* ---------- underground ---------- */

type BB = 'root' | 'ridge' | 'plates' | 'dust';

const burrowIdle = (): Clip<BB> =>
  clip<BB>('idle', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    b.plates.pos(0, 0.015 * Math.sin(p), 0);
    b.dust.scale(0.7 + 0.1 * Math.sin(p));
  }, (t) => {
    const s = Math.sin(phase(t, 2.4) * TAU);
    return [1 - 0.02 * s, 1 + 0.04 * s, 1];
  });

/** Tunnelling: a push every 0.45 s heaves the ridge up and forward, the fins rise a beat after and the dust bursts. */
const burrowWalk = (dur = 0.9): Clip<BB> =>
  clip<BB>('walk', dur, true, (b, t) => {
    const p = phase(t, dur) * TAU;
    const push = Math.max(0, Math.sin(2 * p)) ** 2;
    const late = Math.max(0, Math.sin(2 * p - 0.9)) ** 2;
    b.ridge.rot(-0.04 * push, 0.03 * Math.sin(p), 0).pos(0, 0, 0.03 * push);
    b.plates.pos(0, -0.04 + 0.09 * late, 0.02 * late).rot(0.12 * late, 0, 0.06 * Math.sin(p));
    b.dust.scale(0.6 + 0.6 * late);
    b.root.pos(0.01 * Math.sin(p * 3), 0, 0);
  }, (t) => {
    const p = phase(t, dur) * TAU;
    const push = Math.max(0, Math.sin(2 * p)) ** 2;
    return [1 - 0.05 * push, 1 + 0.22 * push, 1 + 0.06 * push];
  }, 'Avanza bajo tierra');

/** Killed under the ground (or gone back down): the ridge slumps flat and the fins sink. */
const burrowDie = (): Clip<BB> =>
  clip<BB>('die', 1.2, false, (b, t) => {
    const k = span(t, 0, 1.0, inOut);
    b.plates.pos(0, -0.3 * k, 0).rot(0.4 * k, 0, 0);
    b.dust.scale(1 + 0.8 * envelope(t, 0, 0.2, 0.5, 1.1) - 0.95 * span(t, 0.7, 1.2));
    b.ridge.pos(0, -0.12 * k, 0);
  }, (t) => {
    const k = span(t, 0, 1.0, inCubic);
    return [1 + 0.15 * k, 1 - 0.7 * k, 1 + 0.1 * k];
  });

/** Clips by library kind (see ../rigs.ts). */
export const shellSerpentClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), bite(), emerge(), dive()] as Clip[];
export const shellSerpentBurrowClips = (): Clip[] => [burrowIdle(), burrowWalk(), burrowDie()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { shellSerpent: shellSerpentClips, shellSerpentBurrow: shellSerpentBurrowClips };
